const unwrap = (payload) => payload?.data ?? payload ?? {};

const toNumber = (value) => {
	const num = Number(value);
	return Number.isFinite(num) ? num : null;
};

const FALLBACK_TITLES = {
	progress_q1: "Apakah siswa siap mengikuti pembelajaran pada pertemuan hari ini?",
	progress_q2:
		"Apakah siswa mampu mengikuti pembelajaran dengan fokus selama sebagian besar sesi?",
	progress_q3:
		"Apakah siswa mampu mengikuti materi sesuai dengan target pembelajaran hari ini?",
	progress_q4:
		"Apakah siswa memahami konsep utama dari materi yang dipelajari hari ini?",
	progress_q5:
		"Apakah siswa mampu menjelaskan kembali materi dengan bahasanya sendiri?",
	progress_q6: "Apakah siswa mampu mengerjakan latihan secara mandiri?",
	progress_q7:
		"Apakah siswa mampu menerapkan materi pada soal atau contoh yang berbeda?",
	progress_q8:
		"Apakah siswa aktif merespons, bertanya, atau berdiskusi selama pembelajaran?",
	progress_q9:
		"Apakah siswa menunjukkan peningkatan pemahaman selama pertemuan hari ini?",
	progress_q10:
		"Apakah siswa sudah cukup menguasai materi untuk melanjutkan ke materi atau tingkat kesulitan berikutnya?",
};

const PROGRESS_CODES = Object.keys(FALLBACK_TITLES);

const questionNumber = (code) => {
	const match = String(code ?? "").match(/q(\d+)$/i);
	return match ? Number(match[1]) : 0;
};

const pickTitle = (code, item = {}) =>
	item.question_title ??
	item.title ??
	item.text ??
	FALLBACK_TITLES[code] ??
	code ??
	"-";

const normalizePagination = (rawPayload, body) => {
	const pagination =
		body.pagination ??
		body.meta ??
		rawPayload?.pagination ??
		rawPayload?.meta ??
		null;

	if (!pagination) return null;

	return {
		current_page: pagination.current_page ?? pagination.currentPage ?? 1,
		last_page: pagination.last_page ?? pagination.lastPage ?? 1,
		per_page: pagination.per_page ?? pagination.perPage ?? 15,
		total: pagination.total ?? 0,
		from: pagination.from ?? 1,
		to: pagination.to ?? 0,
	};
};

const normalizeAverageItem = (code, raw) => {
	if (raw == null || typeof raw !== "object") {
		return {
			code,
			title: pickTitle(code),
			average: toNumber(raw),
		};
	}

	const resolvedCode =
		raw.question_code ?? raw.code ?? raw.key ?? code ?? "unknown";

	return {
		code: resolvedCode,
		title: pickTitle(resolvedCode, raw),
		average: toNumber(
			raw.average ?? raw.avg ?? raw.mean ?? raw.value ?? raw.score
		),
	};
};

const averagesFromObject = (source = {}) => {
	const keys = Object.keys(source);
	const progressKeys = keys.filter((key) => /^progress_q\d+$/i.test(key));
	const orderedKeys =
		progressKeys.length > 0
			? progressKeys.sort((a, b) => questionNumber(a) - questionNumber(b))
			: PROGRESS_CODES.filter((code) => code in source);

	if (orderedKeys.length === 0 && keys.length > 0) {
		return keys.map((key) => normalizeAverageItem(key, source[key]));
	}

	return orderedKeys.map((code) => normalizeAverageItem(code, source[code]));
};

const averagesFromArray = (items = []) =>
	[...items]
		.map((item, index) =>
			normalizeAverageItem(
				item?.question_code ?? item?.code ?? `progress_q${index + 1}`,
				item
			)
		)
		.sort((a, b) => questionNumber(a.code) - questionNumber(b.code));

export const normalizeAnalytics = (rawPayload) => {
	const body = unwrap(rawPayload);
	const averagesSource = body.averages ?? body.average ?? body.questions ?? [];

	let averages = [];
	if (Array.isArray(averagesSource)) {
		averages = averagesFromArray(averagesSource);
	} else if (averagesSource && typeof averagesSource === "object") {
		averages = averagesFromObject(averagesSource);
	}

	if (averages.length === 0) {
		averages = PROGRESS_CODES.map((code) => normalizeAverageItem(code, null));
	}

	return {
		totalSessions: Number(
			body.total_sessions ??
				body.totalSessions ??
				body.total ??
				body.count ??
				0
		),
		averages,
	};
};

const pickCheckoutAt = (row = {}) =>
	row.check_out_time ??
	row.checkout_at ??
	row.checked_out_at ??
	row.completed_at ??
	row.submitted_at ??
	row.created_at ??
	null;

const pickTentorName = (row = {}) =>
	row.tentor_name ??
	row.tentor?.name ??
	row.mentor_name ??
	row.mentor?.name ??
	row.teacher_name ??
	"-";

const pickMemberName = (row = {}) =>
	row.member_name ??
	row.student_name ??
	row.tentor_member?.name ??
	row.member?.name ??
	row.user?.name ??
	row.siswa_name ??
	"-";

const pickNote = (row = {}) => row.note ?? row.notes ?? row.keterangan ?? "";

const pickAverageScore = (row = {}) =>
	toNumber(
		row.average_score ??
			row.average ??
			row.avg_score ??
			row.session_average ??
			row.mean
	);

export const normalizeSessionsList = (rawPayload) => {
	const body = unwrap(rawPayload);
	const rows = Array.isArray(body) ? body : body.data ?? body.sessions ?? [];
	const pagination = normalizePagination(rawPayload, body);

	const data = rows.map((row) => ({
		id: row.id ?? row.session_id ?? row.schedule_id,
		tentorName: pickTentorName(row),
		memberName: pickMemberName(row),
		checkoutAt: pickCheckoutAt(row),
		note: pickNote(row),
		averageScore: pickAverageScore(row),
	}));

	return { data, pagination };
};

const normalizeAnswers = (answers = []) =>
	[...answers]
		.map((item, index) => {
			const code =
				item?.question_code ?? item?.code ?? `progress_q${index + 1}`;
			const answerValue = toNumber(item?.answer ?? item?.value ?? item?.score);
			return {
				code,
				title: pickTitle(code, item),
				answer: answerValue,
				displayAnswer:
					answerValue != null ? `${answerValue} / 5` : String(item?.answer ?? "-"),
			};
		})
		.sort((a, b) => questionNumber(a.code) - questionNumber(b.code));

export const normalizeSessionDetail = (rawPayload) => {
	const body = unwrap(rawPayload);
	const session = body.session ?? body.schedule ?? body;
	const answers =
		body.answers ??
		session.answers ??
		body.responses ??
		session.responses ??
		[];

	return {
		id: session.id ?? body.id ?? body.session_id,
		tentorName: pickTentorName(session) !== "-" ? pickTentorName(session) : pickTentorName(body),
		memberName:
			pickMemberName(session) !== "-"
				? pickMemberName(session)
				: pickMemberName(body),
		checkoutAt: pickCheckoutAt(session) ?? pickCheckoutAt(body),
		note: pickNote(session) || pickNote(body),
		averageScore: pickAverageScore(session) ?? pickAverageScore(body),
		answers: normalizeAnswers(answers),
	};
};
